import { DisconnectOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useApolloClient, useReactiveVar } from '@apollo/client';
import { Layout, Menu, MenuProps, Row, Col, Tooltip, Space } from 'antd';
import { SiderProps } from 'antd/lib/layout/Sider';
import { LOCALE_STORAGE_KEYS } from 'config/localStorageKeys';
import { Routes } from 'config/routes';
import { PermissionsEnum, useGetAppVersionQuery } from 'gql/types';
import { LOGOUT_MUTATION } from 'modules/auth/graphql/mutations';
import PageContainer from 'modules/common/components/PageContainer';
import useOnlineStatus from 'modules/common/hooks/useOnlineStatus';
import { useRecruiterDashboardAccess } from 'modules/dashboard/hooks/useRecruiterDashboardAccess';
import { cacheState } from 'modules/donor/components/CacheManagement/var';
import NotificationsWrapper from 'modules/navigation/components/NotificationsWrapper';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { cachePersistor } from 'providers/graphql';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { getMenuItemsByRole } from './menuItems';
import { getSelectedMenuKey } from './menuItems/getSelectedKey';
import styles from './styles.module.scss';

import RestrictedLink from '../../../common/components/RestrictedLink';

const { Content, Sider, Header } = Layout;

const defaultSiderProps: SiderProps = {
  theme: 'dark',
  collapsedWidth: '0',
  className: styles.sider,
  width: 300,
  collapsible: true,
};

type PropTypes = {
  children?: ReactNode;
  title?: string;
  tip?: string;
};

const ProfileSideMenuWrapper: FC<PropTypes> = ({ children, title, tip }) => {
  const { t } = useTranslation('navigation.ProfileSideMenuWrapper');
  const location = useLocation();
  const navigate = useNavigate();
  const getAppVersionQuery = useGetAppVersionQuery({
    fetchPolicy: 'cache-first',
  });

  // Check URL for split/inline/trends compare mode parameter
  const searchParams = new URLSearchParams(location.search);
  const displayMode = searchParams.get('displayMode');
  const isCompareMode =
    displayMode === 'split' ||
    displayMode === 'inline' ||
    displayMode === 'trends';

  // Auto-collapse sidebar on Operations Dashboard routes, remember user override
  const isOpsDashboard = location.pathname.startsWith(
    Routes.DashboardsOperations
  );
  const opsSidebarKey = LOCALE_STORAGE_KEYS.OPS_SIDEBAR_COLLAPSED;

  const [collapsed, setCollapsed] = useState(() => {
    if (isOpsDashboard) {
      const stored = localStorage.getItem(opsSidebarKey);
      return stored !== null ? stored === 'true' : true;
    }
    // Collapse if window is narrow OR in split/inline/trends mode
    return window.innerWidth < 1200 || isCompareMode;
  });
  const [userPreferredCollapsed, setUserPreferredCollapsed] = useState(false);
  const [wasNarrow, setWasNarrow] = useState(
    () => window.innerWidth < 1200 || isCompareMode
  );
  const isOnline = useOnlineStatus();
  const backendAppVersion = getAppVersionQuery.data?.getAppVersion;
  const currentUser = useCurrentUser();
  const siderRef = useRef<any>(null);
  const printModEnabledRef = useRef<any>(false);
  const client = useApolloClient();
  const [logout] = useMutation(LOGOUT_MUTATION, {
    onCompleted: async () => {
      localStorage.removeItem(LOCALE_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(LOCALE_STORAGE_KEYS.REFRESH_TOKEN);
      currentUser.cleanData();
      await cachePersistor.purge();
      await client.clearStore();
    },
  });
  const isCacheEnabled = useReactiveVar(cacheState);
  const logoutHandler = async () => {
    await client.stop();
    await logout();
    navigate(Routes.Login);
  };
  const userRole = currentUser.data?.role;
  const userPermissions = currentUser.data?.permissions;
  const { hasAccess: hasRecruiterDashboardAccess } =
    useRecruiterDashboardAccess();

  const menuItems = useMemo(() => {
    if (!userRole || !userPermissions) {
      return [];
    }
    const menuItemsByRole = getMenuItemsByRole({
      userRole,
      permissions: userPermissions,
    });

    // Filter out Recruiter Dashboard if user doesn't have access
    if (!hasRecruiterDashboardAccess) {
      return menuItemsByRole.map((item) => {
        if (item.children) {
          return {
            ...item,
            children: item.children.filter(
              (child) => child.key !== Routes.DashboardsRecruiter
            ),
          };
        }
        return item;
      });
    }

    return menuItemsByRole;
  }, [userRole, hasRecruiterDashboardAccess]);

  const selectedKeys = useMemo(
    () => getSelectedMenuKey(location.pathname),
    [location.pathname]
  );

  const getActiveParentKeys = useCallback(() => {
    return menuItems
      .filter((item) =>
        item.children?.some((child) =>
          selectedKeys.includes(child.key as string)
        )
      )
      .map((item) => item.key as string);
  }, [menuItems, selectedKeys]);

  const [openKeys, setOpenKeys] = useState<string[]>(getActiveParentKeys);

  useEffect(() => {
    const currentParentKeys = getActiveParentKeys();
    if (currentParentKeys.length > 0) {
      setOpenKeys((prevKeys) =>
        Array.from(new Set([...prevKeys, ...currentParentKeys]))
      );
    }
  }, [getActiveParentKeys]);

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    setOpenKeys(keys);
  };

  const defaultMenuProps: MenuProps = {
    selectedKeys,
    openKeys,
    onOpenChange: handleOpenChange,
    theme: 'dark',
    mode: 'inline',
    className: styles.menu,
  };

  const handleClickOutside = (event: MouseEvent) => {
    // Auto-close sidebar when clicking outside on narrow windows
    if (
      siderRef.current &&
      !siderRef.current.contains(event.target) &&
      window.innerWidth < 1200
    ) {
      setCollapsed(true);
    }
  };

  const handleAfterPrint = (_event: Event) => {
    printModEnabledRef.current = false;
  };

  const handleBeforePrint = (_event: Event) => {
    printModEnabledRef.current = true;
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    window.addEventListener('afterprint', handleAfterPrint, true);
    window.addEventListener('beforeprint', handleBeforePrint, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      window.removeEventListener('afterprint', handleAfterPrint, true);
      window.removeEventListener('beforeprint', handleBeforePrint, true);
    };
  }, []);

  // Auto-collapse when entering Ops Dashboard, restore saved preference
  const prevIsOpsRef = useRef(isOpsDashboard);
  useEffect(() => {
    if (isOpsDashboard && !prevIsOpsRef.current) {
      // Just entered ops dashboard — collapse (or use stored preference)
      const stored = localStorage.getItem(opsSidebarKey);
      setCollapsed(stored !== null ? stored === 'true' : true);
    } else if (!isOpsDashboard && prevIsOpsRef.current) {
      // Just left ops dashboard — restore normal preference
      setCollapsed(window.innerWidth < 1200 ? true : userPreferredCollapsed);
    }
    prevIsOpsRef.current = isOpsDashboard;
  }, [isOpsDashboard]);

  useLayoutEffect(() => {
    const handleResize = () => {
      const isNarrow = window.innerWidth < 1200 || isCompareMode;

      if (isNarrow) {
        // Auto-collapse for narrow window or compare mode (split/inline/trends)
        setWasNarrow(true);
        setCollapsed(true);
      } else if (wasNarrow && !isNarrow) {
        // Window expanded and not in compare mode - restore user preference
        setWasNarrow(false);
        setCollapsed(userPreferredCollapsed);
      }
    };
    // Run immediately to catch state on mount and when compare mode changes
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [wasNarrow, userPreferredCollapsed, isCompareMode]);

  const receiveNotifications = currentUser.isCan(
    PermissionsEnum.ReceiveNotifications
  );
  //@ts-ignore
  const version = __VERSION__ as string | undefined;
  //@ts-ignore
  const buildCommit = __BUILD_COMMIT__ as string | undefined;
  return (
    <PageContainer>
      <Header className={styles.header}>
        <Row align="middle">
          <Col>
            <div className={styles.logo}>
              <img
                src="https://navbb-assets.s3.amazonaws.com/logo.webp"
                alt="logo"
              />
            </div>
          </Col>
          <Col>
            <Space align="center" size={15} className={styles.titleWrapper}>
              <h1 className={styles.title}>{title}</h1>
              {tip ? (
                <Tooltip
                  overlay={<>{tip}</>}
                  placement="right"
                  overlayClassName={styles.pageTip}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              ) : undefined}
            </Space>
          </Col>
          {receiveNotifications && (
            <Col flex="1 1 auto" className={styles.notificationWrapper}>
              <NotificationsWrapper />
            </Col>
          )}
        </Row>
      </Header>
      <Layout hasSider>
        <Sider
          {...defaultSiderProps}
          ref={siderRef}
          collapsed={collapsed}
          style={isOpsDashboard ? { background: '#130D1E' } : undefined}
          zeroWidthTriggerStyle={{
            top: 0,
            left: collapsed ? 0 : 264,
            right: 'auto',
            opacity: collapsed ? 0.25 : 1,
          }}
          onCollapse={(value, _type) => {
            if (!printModEnabledRef.current) {
              setCollapsed(value);
              // Persist sidebar state for Ops Dashboard
              if (isOpsDashboard) {
                localStorage.setItem(opsSidebarKey, String(value));
              }
              // Track user preference when window is wide enough
              if (window.innerWidth >= 1200) {
                setUserPreferredCollapsed(value);
              }
            }
          }}
        >
          <Menu
            {...defaultMenuProps}
            className={styles.menu}
            style={isOpsDashboard ? { background: '#130D1E' } : undefined}
          >
            {menuItems.map(({ label, children, ...restProps }) => {
              if (children?.length) {
                return (
                  <Menu.SubMenu key={restProps.key} title={label}>
                    {children.map((props) => {
                      return (
                        <Menu.Item key={props.key}>
                          <Link to={`${props.key}`}>{props.label}</Link>
                        </Menu.Item>
                      );
                    })}
                  </Menu.SubMenu>
                );
              }
              return (
                <Menu.Item {...restProps}>
                  <Link to={`${restProps.key}`}>{label}</Link>
                </Menu.Item>
              );
            })}
            <Menu.Item
              key="logout"
              danger
              onClick={logoutHandler}
              className={styles.logoutButton}
            >
              {t('logout')}
            </Menu.Item>
          </Menu>
          <div
            className={styles.version}
            style={isOpsDashboard ? { backgroundColor: '#130D1E' } : undefined}
          >
            <Row justify="space-between">
              <Col span={18}>
                <Row>
                  <Col span={6}>{t('client')}: </Col>
                  <Col span={18}>
                    v{version}
                    {buildCommit ? ` · ${buildCommit}` : ''}
                  </Col>
                  <Col span={6}>{t('server')}: </Col>
                  <Col span={18}>v{backendAppVersion}</Col>
                  <Col span={18}>
                    <RestrictedLink
                      to={Routes.AppointmentsDonors}
                      permissions={[PermissionsEnum.ViewTheListOfDonors]}
                    >
                      {isCacheEnabled ? t('cacheEnabled') : t('cacheDisabled')}
                    </RestrictedLink>
                  </Col>
                </Row>
              </Col>
              {!isOnline && (
                <Col offset={1} className={styles.iconContainer}>
                  <DisconnectOutlined
                    className={styles.disconnectIcon}
                    title="Offline"
                  />
                </Col>
              )}
            </Row>
          </div>
        </Sider>
        <Content className={styles.content}>{children}</Content>
      </Layout>
    </PageContainer>
  );
};

export default ProfileSideMenuWrapper;
